import { buildBeatGrid } from '@swarasense/shared';
import type { Beat, Thaalam } from '@swarasense/shared';

/**
 * Thaalam metronome (FR-8). Uses the "look-ahead scheduler" pattern: a coarse timer
 * schedules precise click times on the Web Audio clock a little ahead of real time,
 * which keeps timing tight regardless of main-thread jitter (NFR-P2). Tempo changes
 * apply at the next cycle boundary (AC-8.3).
 */
export interface MetronomeCallbacks {
  onBeat?: (beat: Beat, when: number) => void;
}

const LOOK_AHEAD_MS = 25;
const SCHEDULE_AHEAD_S = 0.1;

export class Metronome {
  private ctx: AudioContext | undefined;
  private master: GainNode | undefined;
  private volume = 0.6;
  private grid: Beat[] = [];
  private beatIndex = 0;
  private nextNoteTime = 0;
  private currentBpm = 60;
  private pendingBpm: number | undefined;
  private timer: ReturnType<typeof setInterval> | undefined;
  private onBeat: ((beat: Beat, when: number) => void) | undefined;

  get running(): boolean {
    return this.ctx !== undefined;
  }

  /** Overall thaalam loudness (0–1). Applies live while running. */
  setVolume(v: number): void {
    this.volume = v;
    if (this.master) this.master.gain.value = v;
  }

  async start(
    thaalam: Thaalam,
    bpm: number,
    callbacks: MetronomeCallbacks = {},
    volume?: number,
  ): Promise<void> {
    if (this.ctx) return;
    if (volume !== undefined) this.volume = volume;
    const ctx = new AudioContext();
    await ctx.resume();
    this.master = ctx.createGain();
    this.master.gain.value = this.volume;
    this.master.connect(ctx.destination);
    this.ctx = ctx;
    this.grid = buildBeatGrid(thaalam);
    this.currentBpm = bpm;
    this.beatIndex = 0;
    this.onBeat = callbacks.onBeat;
    this.nextNoteTime = ctx.currentTime + 0.05;
    this.timer = setInterval(() => this.scheduler(), LOOK_AHEAD_MS);
  }

  /** Change tempo; takes effect at the next sam (cycle boundary) — AC-8.3. */
  setTempo(bpm: number): void {
    this.pendingBpm = bpm;
  }

  /** Change thaalam; the new grid takes effect at the next cycle boundary. */
  private pendingGrid: Beat[] | undefined;
  setThaalam(thaalam: Thaalam): void {
    this.pendingGrid = buildBeatGrid(thaalam);
  }

  private scheduler(): void {
    const ctx = this.ctx;
    if (!ctx) return;
    while (this.nextNoteTime < ctx.currentTime + SCHEDULE_AHEAD_S) {
      const beat = this.grid[this.beatIndex]!;
      this.click(beat, this.nextNoteTime);
      this.onBeat?.(beat, this.nextNoteTime);

      this.nextNoteTime += 60 / this.currentBpm;
      this.beatIndex++;
      if (this.beatIndex >= this.grid.length) {
        this.beatIndex = 0;
        // Apply pending tempo/thaalam changes exactly at the cycle boundary.
        if (this.pendingBpm !== undefined) {
          this.currentBpm = this.pendingBpm;
          this.pendingBpm = undefined;
        }
        if (this.pendingGrid) {
          this.grid = this.pendingGrid;
          this.pendingGrid = undefined;
        }
      }
    }
  }

  private click(beat: Beat, when: number): void {
    const ctx = this.ctx!;
    const osc = ctx.createOscillator();
    const gain = ctx.createGain();
    // Distinguish sam / anga / regular beat by pitch and loudness (AC-8.2).
    const freq = beat.type === 'sam' ? 880 : beat.type === 'anga' ? 660 : 440;
    const level = beat.type === 'sam' ? 0.5 : beat.type === 'anga' ? 0.35 : 0.22;
    osc.frequency.value = freq;
    gain.gain.setValueAtTime(level, when);
    gain.gain.exponentialRampToValueAtTime(0.0001, when + 0.05);
    osc.connect(gain);
    gain.connect(this.master ?? ctx.destination);
    osc.start(when);
    osc.stop(when + 0.06);
  }

  async stop(): Promise<void> {
    if (this.timer !== undefined) clearInterval(this.timer);
    this.timer = undefined;
    await this.ctx?.close();
    this.ctx = undefined;
    this.master = undefined;
    this.onBeat = undefined;
    this.pendingBpm = undefined;
    this.pendingGrid = undefined;
  }
}
